/**
 * Browser tabular parser for Lot 6.6A prospect import.
 * Handles TSV, comma CSV, semicolon CSV, quoted fields, and quoted newlines.
 * Not a naive split(",") / split("\\n") parser.
 */

export const DELIMITERS = Object.freeze({
  auto: "auto",
  tab: "\t",
  comma: ",",
  semicolon: ";",
});

export const DELIMITER_OPTIONS = Object.freeze(["auto", "tab", "comma", "semicolon"]);

const DELIMITER_CHARS = Object.freeze({
  tab: "\t",
  comma: ",",
  semicolon: ";",
});

/**
 * RFC4180-style parse for a single delimiter. Quoted fields may contain
 * the delimiter, quotes (doubled), and newlines.
 * @param {string} text
 * @param {string} delimiter
 * @returns {string[][]}
 */
export function parseDelimitedText(text, delimiter) {
  const source = String(text ?? "");
  const rows = [];
  let row = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < source.length; i += 1) {
    const ch = source[i];
    if (quoted) {
      if (ch === '"') {
        if (source[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"') {
      quoted = true;
      continue;
    }
    if (ch === delimiter) {
      row.push(field);
      field = "";
      continue;
    }
    if (ch === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
      continue;
    }
    if (ch === "\r") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
      if (source[i + 1] === "\n") i += 1;
      continue;
    }
    field += ch;
  }
  if (quoted) {
    field = field.trimEnd();
  }
  if (field.length || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

function nonEmptyRows(rows) {
  return rows.filter((row) => Array.isArray(row) && row.some((cell) => String(cell ?? "").trim() !== ""));
}

function scoreDelimiter(text, delimiter) {
  const parsed = nonEmptyRows(parseDelimitedText(text, delimiter).slice(0, 12));
  if (!parsed.length) return { delimiter, score: 0, columns: 0, consistent: 0 };
  const widths = parsed.map((row) => row.length);
  const max = Math.max(...widths);
  const consistent = widths.filter((width) => width === max).length / widths.length;
  const score = max <= 1 ? consistent * 0.1 : max * consistent;
  return { delimiter, score, columns: max, consistent };
}

/**
 * @param {string} text
 * @returns {{ delimiter: string, key: 'tab' | 'comma' | 'semicolon', ambiguous: boolean }}
 */
export function detectDelimiter(text) {
  const sample = String(text ?? "");
  const ranked = ["\t", ",", ";"]
    .map((delimiter) => scoreDelimiter(sample, delimiter))
    .sort((a, b) => b.score - a.score);
  const best = ranked[0] || { delimiter: ",", score: 0 };
  const second = ranked[1] || { delimiter: ",", score: -1 };
  const ambiguous = best.score > 0 && Math.abs(best.score - second.score) < 0.15 && second.columns > 1;
  const key = best.delimiter === "\t" ? "tab" : best.delimiter === ";" ? "semicolon" : "comma";
  return { delimiter: best.delimiter, key, ambiguous };
}

export function resolveDelimiter(text, selected = "auto") {
  if (selected && selected !== "auto" && DELIMITER_CHARS[selected]) {
    return { delimiter: DELIMITER_CHARS[selected], key: selected, ambiguous: false };
  }
  return detectDelimiter(text);
}

const EXCEL_SEP_PREAMBLE_RE = /^\s*sep\s*=\s*([,;\t])\s*$/i;

/**
 * Strip Excel Windows CSV `sep=,` preamble (CRLF-safe).
 * @returns {{ text: string, sepDelimiter: string | null }}
 */
export function stripExcelSepPreamble(text) {
  const source = String(text ?? "");
  const match = source.match(/^([^\r\n]*)(?:\r\n|\n|\r|$)/);
  const firstLine = match ? match[1] : "";
  const sepMatch = firstLine.match(EXCEL_SEP_PREAMBLE_RE);
  if (!sepMatch) return { text: source, sepDelimiter: null };
  const rest = source.slice(match[0].length);
  return { text: rest, sepDelimiter: sepMatch[1] };
}

function sepCharToKey(sepChar) {
  if (sepChar === "\t") return "tab";
  if (sepChar === ";") return "semicolon";
  return "comma";
}

function padRows(rows) {
  const width = rows.reduce((max, row) => Math.max(max, row.length), 0);
  return rows.map((row) => {
    const next = row.map((cell) => String(cell ?? ""));
    while (next.length < width) next.push("");
    return next;
  });
}

export function columnLabel(index, language = "en") {
  let n = index;
  let label = "";
  do {
    label = String.fromCharCode(65 + (n % 26)) + label;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return language === "fr" ? `Colonne ${label}` : `Column ${label}`;
}

const HEADER_HINTS = new Set([
  "adresse",
  "address",
  "street",
  "street address",
  "rue",
  "ville",
  "city",
  "province",
  "prov",
  "state",
  "code postal",
  "postal",
  "postal code",
  "zip",
  "zip code",
  "unites",
  "units",
  "nb unites",
  "nombre de logements",
  "doors",
  "proprietaire",
  "owner",
  "owner name",
  "nom du proprietaire",
  "adresse postale",
  "mailing address",
  "owner address",
  "adresse proprietaire",
  "notes",
  "note",
  "commentaires",
  "comments",
]);

function normalizeHint(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

export function suggestHasHeader(firstRow) {
  if (!Array.isArray(firstRow) || !firstRow.length) return false;
  const hits = firstRow.filter((cell) => HEADER_HINTS.has(normalizeHint(cell))).length;
  if (hits >= 1) return true;
  const looksLikePostal = firstRow.some((cell) => /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(String(cell).trim()));
  if (looksLikePostal) return false;
  return false;
}

/**
 * @param {string} text
 * @param {{ delimiter?: string, hasHeader?: boolean | null, language?: string }} [options]
 */
export function parseProspectImportText(text, { delimiter = "auto", hasHeader = null, language = "en" } = {}) {
  const raw = String(text ?? "").replace(/^\uFEFF/, "");
  if (!raw.trim()) {
    return { ok: false, code: "empty", headers: [], rows: [], delimiterKey: "auto", ambiguous: false, hasHeader: false };
  }
  const stripped = stripExcelSepPreamble(raw);
  const working = stripped.text;
  if (!working.trim()) {
    return { ok: false, code: "empty", headers: [], rows: [], delimiterKey: "auto", ambiguous: false, hasHeader: false };
  }
  let detected = resolveDelimiter(working, delimiter);
  if (stripped.sepDelimiter && delimiter === "auto") {
    detected = {
      delimiter: stripped.sepDelimiter,
      key: sepCharToKey(stripped.sepDelimiter),
      ambiguous: false,
    };
  }
  const parsed = padRows(nonEmptyRows(parseDelimitedText(working, detected.delimiter)));
  if (!parsed.length || !parsed[0].length) {
    return {
      ok: false,
      code: "empty",
      headers: [],
      rows: [],
      delimiterKey: detected.key,
      ambiguous: detected.ambiguous,
      hasHeader: false,
    };
  }
  const headerRow = parsed[0];
  const useHeader = hasHeader == null ? suggestHasHeader(headerRow) : Boolean(hasHeader);
  const headers = useHeader
    ? headerRow.map((cell, index) => String(cell).trim() || columnLabel(index, language))
    : headerRow.map((_, index) => columnLabel(index, language));
  const dataRows = (useHeader ? parsed.slice(1) : parsed).map((row) => {
    const next = row.slice(0, headers.length);
    while (next.length < headers.length) next.push("");
    return next;
  });
  if (!dataRows.length) {
    return {
      ok: false,
      code: "empty",
      headers,
      rows: [],
      delimiterKey: detected.key,
      ambiguous: detected.ambiguous,
      hasHeader: useHeader,
    };
  }
  return {
    ok: true,
    code: null,
    headers,
    rows: dataRows,
    delimiterKey: detected.key,
    ambiguous: detected.ambiguous,
    hasHeader: useHeader,
  };
}

export function isCsvFilename(name) {
  if (typeof name !== "string") return false;
  return name.trim().toLowerCase().endsWith(".csv");
}

export const IMPORT_FILE_ACCEPT =
  ".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xls,application/vnd.ms-excel";

export const ADMIN_PROSPECT_IMPORT_MAX_FILE_BYTES = 8 * 1024 * 1024;

export function isExcelFilename(name) {
  if (typeof name !== "string") return false;
  const lower = name.trim().toLowerCase();
  return /\.xlsx$/i.test(lower) || /\.xls$/i.test(lower);
}

export function importFileTypeFromName(name) {
  if (typeof name !== "string") return null;
  const lower = name.trim().toLowerCase();
  if (lower.endsWith(".csv")) return "csv";
  if (/\.xlsx$/i.test(lower)) return "xlsx";
  if (/\.xls$/i.test(lower)) return "xls";
  return null;
}

export function excelFileTypeFromName(name) {
  const type = importFileTypeFromName(name);
  return type === "xlsx" || type === "xls" ? type : null;
}

export function csvContentTypeForFileType() {
  return "text/csv";
}

export function excelContentTypeForFileType(fileType) {
  if (fileType === "csv") return "text/csv";
  if (fileType === "xls") return "application/vnd.ms-excel";
  return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
}
